/**
 * Turntable — configuration.
 *
 * Paste your Google credentials below to enable YouTube Music sign-in, your
 * playlists and search. See SETUP.md for the walkthrough. You can also paste
 * them in the app: tap the pill in the top-left, then "Use my own keys".
 * Those are stored in this browser's localStorage only.
 */

export interface Credentials {
  /**
   * OAuth 2.0 Client ID — Google Cloud Console > Credentials.
   * Looks like `123456789012-abcdefghijklmnop.apps.googleusercontent.com`.
   */
  GOOGLE_CLIENT_ID: string;
  /**
   * API key — Google Cloud Console > Credentials, like `AIzaSy…`.
   * Optional: search also works with the signed-in OAuth token.
   */
  YOUTUBE_API_KEY: string;
}

export const DEFAULTS: Credentials = {
  GOOGLE_CLIENT_ID: '',
  YOUTUBE_API_KEY: '',
};

export type Quality = 'auto' | 'high' | 'low';

export interface Prefs {
  /** Cinematic record-swap animation. Off = records snap in instantly. */
  cinematicSwap: boolean;
  /** Map the album artwork over the whole record face (picture disc). */
  pictureDisc: boolean;
  /** Vinyl crackle layer in Demo mode. */
  crackle: boolean;
  defaultVolume: number;
  /** `auto` picks a quality tier from the device; `high`/`low` force it. */
  quality: Quality;
  /** Whether a tablet has had its one-time offer of full screen. */
  fullscreenOffered: boolean;
}

/** Read-only tunables that are not user preferences. */
export const OPTIONS = {
  scope: 'https://www.googleapis.com/auth/youtube.readonly',
  maxResults: 40,
} as const;

export const DEFAULT_PREFS: Prefs = {
  cinematicSwap: true,
  pictureDisc: false,
  crackle: true,
  defaultVolume: 0.85,
  quality: 'auto',
  fullscreenOffered: false,
};

/** The keys that are actually persisted — the OAuth scope is not a preference. */
export const PREF_KEYS = [
  'cinematicSwap',
  'pictureDisc',
  'crackle',
  'defaultVolume',
  'quality',
  'fullscreenOffered',
] as const satisfies readonly (keyof Prefs)[];
